import { AuthPageShell } from "@/auth/AuthPageShell";
import { useAuth } from "@/auth/AuthProvider";
import { Button, Input, Spinner } from "@/components/ui";
import { defaultRawRoute } from "@/raw/datasets/registry";
import { useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";

const MIN_PASSWORD_LENGTH = 8;

export function ResetPasswordPage() {
  const { loading, recoveryMode, session, updatePassword } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (loading) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-[var(--color-surface-muted)]">
        <Spinner label="Verifying reset link…" />
      </div>
    );
  }

  if (session && !recoveryMode) {
    return <Navigate to={defaultRawRoute} replace />;
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setError(null);

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setSubmitting(true);
    const result = await updatePassword(password);
    setSubmitting(false);

    if (result.error) {
      setError(result.error);
      return;
    }

    navigate(defaultRawRoute, { replace: true });
  };

  if (!session) {
    return (
      <AuthPageShell title="Link expired">
        <div className="space-y-4">
          <p className="text-center text-sm text-[var(--color-text-muted)]">
            Request a new reset email and open the latest link.
          </p>
          <Link
            to="/forgot-password"
            className="inline-flex h-9 w-full items-center justify-center rounded-[var(--radius-md)] bg-[var(--color-accent)] text-sm font-medium text-white hover:bg-[var(--color-accent-hover)]"
          >
            Request a new link
          </Link>
          <p className="text-center text-sm text-[var(--color-text-muted)]">
            <Link to="/login" className="font-medium text-[var(--color-accent)] hover:underline">
              Back to sign in
            </Link>
          </p>
        </div>
      </AuthPageShell>
    );
  }

  return (
    <AuthPageShell title="Choose a new password">
      <form className="space-y-4" onSubmit={(event) => void handleSubmit(event)}>
        <label className="block space-y-1.5">
          <span className="text-sm font-medium text-[var(--color-text)]">New password</span>
          <Input
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            minLength={MIN_PASSWORD_LENGTH}
            required
          />
        </label>

        <label className="block space-y-1.5">
          <span className="text-sm font-medium text-[var(--color-text)]">Confirm password</span>
          <Input
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            minLength={MIN_PASSWORD_LENGTH}
            required
          />
        </label>

        <p className="text-xs text-[var(--color-text-muted)]">
          At least {MIN_PASSWORD_LENGTH} characters.
        </p>

        {error ? <p className="text-sm text-[var(--color-danger)]">{error}</p> : null}

        <Button type="submit" className="w-full" disabled={submitting}>
          {submitting ? "Saving…" : "Save password"}
        </Button>
      </form>
    </AuthPageShell>
  );
}
